import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil, tap, throttleTime } from 'rxjs';

import { DeviceClaimingApiService } from '@shure/cloud/device-management/devices/data-access';
import { DeviceClaimProgress, DeviceDetection } from '@shure/cloud/shared/models/devices';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import { PrismProgressIndicatorComponent } from '@shure/prism-angular-components';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AddDevicesDialogService,
	DeviceClaimMethods,
	MacSerialNumberDevice
} from '../../../../services/add-devices-dialog.service';

import { LocalNetworkConfirmationTableComponent } from './local-network-confirmation-table/local-network-confirmation-table.component';
import { MacSerialConfirmationTableComponent } from './mac-serial-confirmation-table/mac-serial-confirmation-table.component';

@Component({
	selector: 'sh-step3-confirmation',
	standalone: true,
	imports: [
		CommonModule,
		TranslocoModule,
		PipesModule,
		LocalNetworkConfirmationTableComponent,
		MacSerialConfirmationTableComponent,
		PrismProgressIndicatorComponent
	],
	templateUrl: './step3-confirmation.component.html',
	styleUrls: ['./step3-confirmation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class Step3ConfirmationComponent implements OnDestroy, AfterViewInit {
	public destroy$: Subject<void> = new Subject<void>();

	public deviceClaimMethod: DeviceClaimMethods | undefined;
	public localNetworkDevicesToClaim: DeviceClaimProgress<DeviceDetection>['devices'][0][] = [];
	public macSerialDevicesToClaim: DeviceClaimProgress<MacSerialNumberDevice>['devices'][0][] = [];
	public claimOpInProgress$ = this.deviceClaimingService.claimOpInProgress$
		.asObservable()
		.pipe(
			tap((result) =>
				this.logger.information(`Claim In Progress (${this.deviceClaimMethod})`, `value: ${result}`)
			)
		);
	public currentClaimProgress: DeviceClaimProgress = {
		done: false,
		stats: {
			totalToDo: 0,
			success: 0,
			inProgress: 0,
			failed: 0
		},
		devices: []
	};
	private logger: ILogger;

	constructor(
		private readonly addDevicesDialogService: AddDevicesDialogService,
		protected readonly deviceClaimingService: DeviceClaimingApiService,
		private changeDetectorRef: ChangeDetectorRef,
		private _logger: ILogger
	) {
		this.logger = this._logger.createScopedLogger('AddDevicesDialogStep3');
	}

	public ngAfterViewInit(): void {
		setTimeout(() => {
			this.addDevicesDialogService.deviceDiscoveryMethod$
				.pipe(takeUntil(this.destroy$))
				.subscribe((claimMethod) => {
					if (claimMethod !== undefined) {
						this.deviceClaimMethod = claimMethod;
						this.startClaim(claimMethod);
					} else {
						throw Error('Unexpected undefind claimmethod');
					}
				});
		});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.addDevicesDialogService.setDetectedClaimableDevices([]);
	}

	public doDeviceDetectionClaim(devices: DeviceDetection[]): void {
		this.deviceClaimingService
			.claimDevicesThrottled$(devices)
			.pipe(takeUntil(this.destroy$))
			.subscribe((progress) => {
				this.localNetworkDevicesToClaim = [...progress.devices];
				this.currentClaimProgress = progress;
				this.changeDetectorRef.detectChanges();
			});
	}

	public doMacSerialClaiming(devices: MacSerialNumberDevice[]): void {
		this.deviceClaimingService
			.claimDevicesByMacSerialThrottled$(devices)
			.pipe(takeUntil(this.destroy$))
			.subscribe((progress) => {
				this.macSerialDevicesToClaim = [...progress.devices];
				this.currentClaimProgress = { ...progress };
				this.changeDetectorRef.detectChanges();
			});
	}

	private startClaim(claimMethod: DeviceClaimMethods): void {
		if (claimMethod === 'ClaimByTokenOnLocalNetwork') {
			this.addDevicesDialogService.detectedClaimableDevices$
				// because there can be a flurry of "progress" reports emitted,
				// and it's not possible to report them all, apply a
				// throttle so the UI looks better.
				.pipe(throttleTime(1000), takeUntil(this.destroy$))
				.subscribe((devicesToClaim) => {
					this.doDeviceDetectionClaim(devicesToClaim);
				});
		} else {
			this.addDevicesDialogService.macSerialNumbersToClaim$
				// because there can be a flurry of "progress" reports emitted,
				// and it's not possible to report them all, apply a
				// throttle so the UI looks better.
				.pipe(throttleTime(1000), takeUntil(this.destroy$))
				.subscribe((macSerialNumbers) => {
					this.doMacSerialClaiming(macSerialNumbers);
				});
		}
	}
}
